import { Bell, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import toast, { type Toast } from 'react-hot-toast';
import { cn } from '@/lib/utils';
import type { NotificationItem } from '../services/notifications.service';

interface NotificationToastProps {
  toastInstance: Toast;
  notification: NotificationItem;
  onOpen?: (() => void) | undefined;
}

/**
 * Real-time push card shown at the portal's top-center when a `notification`
 * WebSocket event arrives. Rendered via `toast.custom(...)`.
 */
export function NotificationToast({
  toastInstance,
  notification,
  onOpen,
}: NotificationToastProps) {
  const relativeTime = (() => {
    try {
      return formatDistanceToNow(new Date(notification.createdAt), {
        addSuffix: true,
      });
    } catch {
      return 'just now';
    }
  })();

  const dismiss = () => toast.dismiss(toastInstance.id);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'pointer-events-auto relative w-[380px] max-w-[92vw] overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-lg ring-1 ring-black/5 transition-all duration-200',
        toastInstance.visible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0',
      )}
    >
      <button
        type="button"
        onClick={() => {
          onOpen?.();
          dismiss();
        }}
        className="flex w-full items-start gap-3 p-4 text-left"
      >
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Bell className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-foreground">
            {notification.subject}
          </span>
          {notification.content ? (
            <span className="mt-0.5 line-clamp-2 block text-xs text-muted-foreground">
              {notification.content}
            </span>
          ) : null}
          <span className="mt-1 block text-[11px] text-muted-foreground">
            {relativeTime}
          </span>
        </span>
      </button>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={dismiss}
        className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
